package dev.lingvo.ops.pages

import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.fillMaxWidth
import com.varabyte.kobweb.compose.ui.modifiers.gap
import com.varabyte.kobweb.core.Page
import com.varabyte.kobweb.core.rememberPageContext
import com.varabyte.kobweb.silk.components.navigation.Link
import dev.lingvo.ops.api.OpsApi
import dev.lingvo.ops.components.OpsPageHeader
import dev.lingvo.ops.components.OpsPanel
import dev.lingvo.ops.components.OpsResourceStates
import dev.lingvo.ops.components.OpsShell
import dev.lingvo.ops.state.rememberOpsPaging
import dev.lingvo.ops.state.rememberOpsResource
import dev.lingvo.shared.Plan
import dev.lingvo.shared.PlanStatus
import dev.lingvo.shared.ops.OpsWorkspaceRowDto
import dev.lingvo.site.ds.LBadge
import dev.lingvo.site.ds.LBadgeVariant
import dev.lingvo.site.ds.LFilterBar
import dev.lingvo.site.ds.LFilterField
import dev.lingvo.site.ds.LSearchField
import dev.lingvo.site.ds.LSelect
import dev.lingvo.site.ds.LSelectOption
import dev.lingvo.site.ds.LTable
import dev.lingvo.site.ds.LTableBody
import dev.lingvo.site.ds.LTableCell
import dev.lingvo.site.ds.LTableHead
import dev.lingvo.site.ds.LTableHeaderCell
import dev.lingvo.site.ds.LTableRow
import dev.lingvo.site.ds.Space
import dev.lingvo.site.ds.formatCount
import dev.lingvo.site.ds.formatUtc
import dev.lingvo.site.nav.UrlState
import org.jetbrains.compose.web.dom.Text

private data class WorkspaceQuery(val search: String, val plan: String, val status: String, val cursor: String?)

@Page("/workspaces")
@Composable
fun WorkspacesPage() {
    val ctx = rememberPageContext()
    var search by remember { mutableStateOf(UrlState.read(ctx, "q").orEmpty()) }
    var plan by remember { mutableStateOf(UrlState.read(ctx, "plan").orEmpty()) }
    var status by remember { mutableStateOf(UrlState.read(ctx, "status").orEmpty()) }
    fun sync() = UrlState.replace(mapOf("q" to search, "plan" to plan, "status" to status))
    OpsShell("Workspaces") {
        val paging = rememberOpsPaging<OpsWorkspaceRowDto>(listOf(search, plan, status))
        val resource = rememberOpsResource(WorkspaceQuery(search, plan, status, paging.cursor)) { query ->
            OpsApi.workspaces(query.plan.takeIf(String::isNotBlank), query.status.takeIf(String::isNotBlank), query.search.takeIf(String::isNotBlank), query.cursor)
        }
        Column(Modifier.fillMaxWidth().gap(Space.XL)) {
            OpsPageHeader("Workspaces", "Inspect workspace access, billing state and service configuration.", resource.state.isRefreshing, resource::refresh)
            LFilterBar(
                listOf(
                    LFilterField("workspace-search", "Search", search.takeIf(String::isNotBlank), { search = ""; sync() }) { modifier ->
                        LSearchField(search, { search = it; sync() }, modifier, "Search workspaces", "Name or slug")
                    },
                    LFilterField("workspace-plan", "Plan", plan.takeIf(String::isNotBlank), { plan = ""; sync() }) { modifier ->
                        LSelect(plan, listOf(LSelectOption("", "Any plan")) + Plan.entries.map { LSelectOption(it.name, it.displayName) }, { plan = it; sync() }, modifier)
                    },
                    LFilterField("workspace-status", "Status", status.takeIf(String::isNotBlank), { status = ""; sync() }) { modifier ->
                        LSelect(status, listOf(LSelectOption("", "Any status")) + PlanStatus.entries.map { LSelectOption(it.name, it.name.replace('_', ' ').lowercase().replaceFirstChar(Char::uppercase)) }, { status = it; sync() }, modifier)
                    },
                ),
                onClearAll = { search = ""; plan = ""; status = ""; sync() },
            )
            OpsPanel("Directory", "Newest workspaces first, with up to 100 rows per page. Counts describe the recorded workspace state.") {
                OpsResourceStates(resource.state, "No workspaces", "No workspace has been recorded.", "No workspace matches these filters.".takeIf { search.isNotBlank() || plan.isNotBlank() || status.isNotBlank() }, retry = resource::refresh) { page ->
                    LTable(caption = "Workspace directory") {
                        LTableHead { LTableRow { listOf("Name", "Plan", "Status", "Members", "Projects", "Ownerless", "Created").forEach { LTableHeaderCell { Text(it) } } } }
                        LTableBody { page.rows.forEach { row -> LTableRow {
                            LTableCell { Link("/workspaces/${row.orgId}") { Text(row.name) } }
                            LTableCell { Text(row.plan.displayName) }
                            LTableCell { Text(row.planStatus.name) }
                            LTableCell { Text(formatCount(row.members)) }
                            LTableCell { Text(formatCount(row.projects)) }
                            LTableCell { if (row.ownerless) LBadge("Ownerless", variant = LBadgeVariant.WARNING) else Text("No") }
                            LTableCell { Text(formatUtc(row.createdAt)) }
                        } } }
                    }
                }
                OpsResourcePagination(paging, resource.state, "workspaces")
            }
        }
    }
}
